import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { OrganizationData, OrganizationPixelMetricData } from '@openx/types/organization.ts';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria.ts';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext.tsx';
import { useGetTableColumns } from '../hooks/useGetTableColumns.tsx';

type OrganizationDetailsProps = {
  organization: OrganizationData;
};

const initialCriteria: AllTableCriteria = {
  [CriteriaDimension.SORT]: {
    column: 'fires_30d',
    direction: SortDirection.DESC,
    sortableColumns: ['event_type_label', 'fires_30d', 'unique_30d', 'last_fire_at'],
  },
};

export const PixelSection = ({ organization }: OrganizationDetailsProps) => {
  const { t } = useTranslation();
  const { pixelMetricsTableColumns } = useGetTableColumns();
  const [criteria, setCriteria] = useState<AllTableCriteria>(initialCriteria);
  const { useOrganizationPixelMetricsFetch } = useOrganizationContext();
  // The datamart is keyed by the org's exchange_account_network_id; only fetch
  // when retargeting is enabled (skips otherwise).
  const { pixelMetrics, loading } = useOrganizationPixelMetricsFetch(
    organization.retargeting_enabled ? organization.exchange_account_network_id : null
  );

  const handleCriteriaChange = useCallback((newCriteria: CriteriaChange<CriteriaDimension>) => {
    setCriteria(prev => applyCriteriaChange(prev, newCriteria));
  }, []);

  const sortedPixelMetrics = useMemo(
    () => clientSideCriteriaProcessing(pixelMetrics ?? [], criteria),
    [pixelMetrics, criteria]
  );

  return (
    <FormSection description={t('Events tracked by the OpenX Pixel for retargeting.')} divider title={t('Pixel')}>
      <FieldDetails fullWidth toggle title={t('Enable Retargeting')} value={!!organization.retargeting_enabled} />
      {organization.retargeting_enabled && (
        <Box data-test="pixel-details">
          {organization.exchange_account_network_id ? (
            <>
              <FieldDetails title={t('Pixel ID')} value={organization.exchange_account_network_id} />
              {!sortedPixelMetrics?.length && !loading ? (
                <Typography data-test="no-pixel-metrics-message" variant="body2" color={'textSecondary'}>
                  {t('No pixel events recorded yet.')}
                </Typography>
              ) : (
                <Table<OrganizationPixelMetricData>
                  name={TableName.PIXEL_METRICS}
                  data={sortedPixelMetrics}
                  columns={pixelMetricsTableColumns}
                  criteria={criteria}
                  onCriteriaChange={handleCriteriaChange}
                  loading={loading}
                  getRowKey={({ event_type }) => event_type}
                  enableTableHeaderCustomization={false}
                />
              )}
            </>
          ) : (
            <Typography data-test="no-pixel-id-message" variant="body2" color={'textSecondary'}>
              {t('No Pixel ID configured for this organization.')}
            </Typography>
          )}
        </Box>
      )}
    </FormSection>
  );
};
